import {
  Link as TanStackLink,
  Navigate,
  Outlet,
  useLocation as useTanStackLocation,
  useNavigate as useTanStackNavigate,
  useParams as useTanStackParams,
  useRouter
} from '@tanstack/react-router'
import type { AnchorHTMLAttributes, ReactNode } from 'react'
import {
  type EnrollmentNavigationState,
  useLocalEnrollmentNavigation
} from './localEnrollmentNavigation'

type NavigationOptions = {
  replace?: boolean
  state?: EnrollmentNavigationState
}

type NavigateTo = string | number

export const useNavigate = () => {
  const router = useRouter()
  const navigate = useTanStackNavigate()
  const localEnrollmentNavigation = useLocalEnrollmentNavigation()

  return (to: NavigateTo, options: NavigationOptions = {}) => {
    if (typeof to === 'number') {
      router.history.go(to)
      return
    }

    if (localEnrollmentNavigation?.canHandlePath(to)) {
      localEnrollmentNavigation.navigate(to, options)
      return
    }

    const nextState = options.state
      ? (previous: Record<string, unknown>) => ({
          ...previous,
          ...options.state
        })
      : undefined

    void navigate({
      to: to as never,
      replace: options.replace,
      state: nextState
    } as never)
  }
}

export const useLocation = () => {
  const location = useTanStackLocation()
  const localEnrollmentNavigation = useLocalEnrollmentNavigation()

  if (localEnrollmentNavigation) {
    return {
      pathname: localEnrollmentNavigation.location.pathname,
      searchStr: localEnrollmentNavigation.location.searchStr,
      state: localEnrollmentNavigation.location.state
    }
  }

  return {
    ...location,
    state: location.state as unknown as EnrollmentNavigationState | null,
    pathname: location.pathname
  }
}

export const useSearchParams = (): [URLSearchParams] => {
  const searchString = useTanStackLocation({
    select: location => location.searchStr
  })
  const localEnrollmentNavigation = useLocalEnrollmentNavigation()

  if (localEnrollmentNavigation) {
    return [new URLSearchParams(localEnrollmentNavigation.location.searchStr)]
  }

  return [new URLSearchParams(searchString)]
}

export const useParams = <TParams extends Record<string, string>>() => {
  const params = useTanStackParams({
    strict: false
  } as never) as TParams
  const localEnrollmentNavigation = useLocalEnrollmentNavigation()

  return (localEnrollmentNavigation?.location.params as TParams | undefined) ?? params
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  to: string
  children?: ReactNode
}

export const Link = ({ to, children, ...props }: LinkProps) => {
  const localEnrollmentNavigation = useLocalEnrollmentNavigation()

  if (localEnrollmentNavigation?.canHandlePath(to)) {
    return (
      <a
        {...props}
        href={to}
        onClick={event => {
          props.onClick?.(event)

          if (
            event.defaultPrevented ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return
          }

          event.preventDefault()
          localEnrollmentNavigation.navigate(to)
        }}
      >
        {children}
      </a>
    )
  }

  return (
    <TanStackLink {...props} to={to as never}>
      {children}
    </TanStackLink>
  )
}

export { Navigate, Outlet }
export type { EnrollmentNavigationState }
